import { useMemo } from 'react';
import {
  useGetAssociatedDevicesInUnitQuery,
  useGetDevicesInOrganisationQuery,
  useGetUnassociatedDevicesQuery,
} from '@/redux/api/endpoints/device';
import {
  DeviceAssignedToOrganisation,
  DeviceAssociatedWithUnit,
  DeviceUnassociated,
  MaybeAssociatedDevice,
} from '@/types/device';
import { isDeviceAssigned } from '@/utils/is-device-assigned';
import { useCurrentOrganisation } from './use-current-organisation';
import { useCurrentUnit } from './use-current-unit';

export const useUnassociatedDevices = () => {
  const { organisation } = useCurrentOrganisation();
  const organisationId = organisation?.id;
  const result = useGetUnassociatedDevicesQuery({ organisationId });

  const data = useMemo<DeviceUnassociated[]>(
    () => result.currentData ?? [],
    [result.currentData],
  );

  return { ...result, data };
};

export const useUnassociatedDevicesInCurrentOrganisation = () => {
  const { isUsingOrganisationOverride, organisation } = useCurrentOrganisation(); // prettier-ignore
  const organisationId = organisation?.id;

  const result = useGetUnassociatedDevicesQuery({ organisationId });

  const data = useMemo<DeviceAssignedToOrganisation[]>(() => {
    let data = (result.currentData ?? []).filter(isDeviceAssigned);

    if (isUsingOrganisationOverride) {
      data = data.filter((device) =>
        'organisation' in device
          ? device.organisation?.id === organisationId
          : false,
      );
    }

    return data;
  }, [isUsingOrganisationOverride, organisationId, result.currentData]);

  return { ...result, data };
};

export const useDevicesInCurrentOrganisation = () => {
  const { organisation } = useCurrentOrganisation();
  const result = useGetDevicesInOrganisationQuery(
    { organisation: organisation! },
    {
      skip: organisation === undefined,
      pollingInterval: 60000,
    },
  );

  const data = useMemo<MaybeAssociatedDevice[]>(
    () =>
      [...(result.currentData ?? [])].sort((a, b) =>
        (a.meta?.serialNumber ?? '').localeCompare(b.meta?.serialNumber ?? ''),
      ),
    [result.currentData],
  );

  const isFetching = result.isFetching;
  const isLoading = result.isLoading;

  return {
    data,
    isFetching,
    isLoading,
  };
};

export const useAssociatedDevicesInCurrentUnit = () => {
  const { data: unit } = useCurrentUnit();

  const result = useGetAssociatedDevicesInUnitQuery(
    { unit: unit! },
    { skip: unit === undefined, pollingInterval: 60000 },
  );

  const data = useMemo<DeviceAssociatedWithUnit[]>(
    () => result.currentData ?? [],
    [result.currentData],
  );

  return { ...result, data };
};
